import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Calendar, Phone, Mail, ArrowRight, Loader2 } from "lucide-react";
import SEOHead from "../components/SEOHead";

const API = process.env.REACT_APP_BACKEND_URL;

export default function DepositSuccess() {
 const [params] = useSearchParams();
 const sid = params.get("session_id");
 const [status, setStatus] = useState("loading");
 const [order, setOrder] = useState(null);
 const [pollCount, setPollCount] = useState(0);

 useEffect(() => {
  document.title = "Deposit Confirmed | Magic Cruises";
  if (!sid) { setStatus("error"); return; }

  let timer = null;
  let tries = 0;
  const poll = async () => {
   try {
    const { data } = await axios.get(`${API}/api/checkout/status/${sid}`);
    if (data?.payment_status === "paid") {
     setOrder(data);
     setStatus("paid");
     return;
    }
    if (tries++ < 10) {
     setPollCount(tries);
     timer = setTimeout(poll, 2000);
    } else {
     setStatus("timeout");
    }
   } catch {
    if (tries++ < 5) timer = setTimeout(poll, 2500); else setStatus("error");
   }
  };
  poll();
  return () => { if (timer) clearTimeout(timer); };
 }, [sid]);

 const meta = order?.metadata || {};
 const amount = (order?.amount_total || 0) / 100;

 return (
  <main className="bg-gradient-to-b from-emerald-50 via-white to-white min-h-screen pt-20 pb-20" data-testid="deposit-success-page">
   <SEOHead title="Deposit Confirmed" description="Your $1,000 holding deposit secures your Magic Cruises date." />
   <div className="mx-auto max-w-2xl px-5 lg:px-8">
    {status === "loading" && (
     <div className="text-center py-20">
      <Loader2 className="w-10 h-10 text-emerald-600 animate-spin mx-auto" />
      <p className="text-slate-600 mt-4 text-sm">Confirming your payment with Stripe…</p>
      {pollCount > 2 && <p className="text-slate-400 mt-2 text-xs">Still processing… (this can take 10-20 seconds)</p>}
     </div>
    )}

    {status === "paid" && (
     <div className="bg-white rounded-3xl shadow-xl ring-1 ring-emerald-100 p-8 sm:p-12">
      <div className="flex justify-center mb-6">
       <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center">
        <CheckCircle2 className="w-9 h-9 text-emerald-600" strokeWidth={2.5} />
       </div>
      </div>
      <h1 className="text-3xl sm:text-4xl font-black text-slate-900 text-center tracking-tight">Your date is secured.</h1>
      <p className="text-center text-slate-600 mt-3 text-base sm:text-lg">Thank you, <strong>{meta.customer_name}</strong>. Your <strong>${amount.toFixed(2)} AUD</strong> holding deposit has been received.</p>

      <div className="mt-8 rounded-2xl bg-slate-50 ring-1 ring-slate-200 px-6 py-5 space-y-3">
       <div className="text-[10px] uppercase tracking-[0.28em] font-black text-slate-500">Booking Summary</div>
       <Row label="Cruise type" value={meta.cruise_label || meta.cruise_type} />
       <Row label="Date held" value={formatDate(meta.requested_date)} icon={<Calendar className="w-3.5 h-3.5" />} />
       {meta.guests && <Row label="Approx. guests" value={meta.guests} />}
       <Row label="Total paid" value={`$${amount.toFixed(2)} AUD (incl. card fee)`} bold />
       <Row label="Reference" value={(meta.order_id || "").slice(0, 8).toUpperCase()} mono />
      </div>

      <div className="mt-6 rounded-2xl bg-amber-50 ring-1 ring-amber-200 px-6 py-5">
       <div className="text-[10px] uppercase tracking-[0.28em] font-black text-amber-900 mb-2">What happens next</div>
       <ul className="text-sm text-amber-900/90 space-y-1.5 leading-snug">
        <li>· A confirmation email will reach <strong>{meta.customer_email}</strong> shortly.</li>
        <li>· Our team will call you within <strong>24 hours</strong> to finalise package, menu and headcount.</li>
        <li>· Your $1,000 will apply as <strong>credit toward your final cruise cost</strong>.</li>
        <li>· Balance is due <strong>14 days before</strong> your cruise.</li>
       </ul>
      </div>

      <div className="mt-8 flex flex-col sm:flex-row gap-3 text-center">
       <a href="tel:1300100502" className="flex-1 inline-flex items-center justify-center gap-2 px-5 py-3.5 rounded-full bg-slate-900 hover:bg-slate-800 text-white text-sm font-bold transition-colors" data-testid="success-call-cta">
        <Phone className="w-4 h-4" /> 1300 100 502
       </a>
       <a href="mailto:info@magiccruises.com.au" className="flex-1 inline-flex items-center justify-center gap-2 px-5 py-3.5 rounded-full bg-white ring-1 ring-slate-300 hover:bg-slate-50 text-slate-900 text-sm font-bold transition-colors" data-testid="success-email-cta">
        <Mail className="w-4 h-4" /> Email us
       </a>
      </div>

      <Link to="/" className="mt-6 block text-center text-slate-500 hover:text-slate-700 text-sm underline-offset-2 hover:underline">← Back to home</Link>
     </div>
    )}

    {(status === "timeout" || status === "error") && (
     <div className="bg-white rounded-3xl shadow-xl ring-1 ring-amber-100 p-8 sm:p-12 text-center">
      <h1 className="text-2xl font-black text-slate-900">Hmm, we're still confirming your payment.</h1>
      <p className="text-slate-600 mt-3 text-sm">Stripe sometimes takes a few extra seconds. Your deposit may have already been confirmed.</p>
      <p className="text-slate-600 mt-2 text-sm">Please email us at <strong>info@magiccruises.com.au</strong> with reference <strong className="font-mono">{sid?.slice(0, 12)}</strong> and we'll check on it for you.</p>
      <Link to="/" className="inline-block mt-6 text-slate-500 hover:text-slate-700 text-sm underline-offset-2 hover:underline">← Back to home</Link>
     </div>
    )}
   </div>
  </main>
 );
}

function Row({ label, value, bold, mono, icon }) {
 return (
  <div className="flex items-baseline justify-between gap-4 text-sm">
   <span className="text-slate-500 inline-flex items-center gap-1.5">{icon}{label}</span>
   <span className={`${bold ? "font-black text-slate-900" : "text-slate-700 font-medium"} ${mono ? "font-mono uppercase text-xs" : ""} text-right`}>{value}</span>
  </div>
 );
}

function formatDate(iso) {
 if (!iso) return "";
 const d = new Date(iso + "T00:00:00");
 return d.toLocaleDateString("en-AU", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
}
